// pricing-calculator/utils/money.ts
// Money formatting helpers (UI-agnostic). We treat values as major currency units (e.g., 3000 => $3,000).

import type { Currency } from '@dopomogai/types';

type FmtOpts = {
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/**
 * Formats a number as currency in major units (e.g., 3000 -> $3,000).
 */
export function fmtMoney(value: number, currency: Currency = 'USD', opts: FmtOpts = {}): string {
  const {
    locale = 'en-US',
    minimumFractionDigits,
    maximumFractionDigits = 0, // default to no cents, per calculator UI
  } = opts;

  const nf = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return nf.format(Number.isFinite(value) ? value : 0);
}

/**
 * Compact currency formatting (e.g., $3.2K, $1.5M).
 * Note: Not all runtimes fully support compact notation with currency.
 */
export function fmtMoneyCompact(value: number, currency: Currency = 'USD', locale = 'en-US'): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(Number.isFinite(value) ? value : 0);
  } catch {
    // Fallback if compact notation with currency is not supported
    return fmtMoney(value, currency);
  }
}

/**
 * Safe adder for a list of prices (major units).
 */
export function sumPrices(prices: number[] = []): number {
  return prices.reduce((acc, n) => (Number.isFinite(n) ? acc + n : acc), 0);
}

/**
 * Ensures a finite money number; useful before formatting.
 */
export function toFiniteMoney(n: unknown, fallback = 0): number {
  const v = typeof n === 'string' ? Number(n) : (n as number);
  return Number.isFinite(v) ? v : fallback;
}
